import React, {useEffect, useMemo, useState} from "react";
import axios from "../axios/axios";
import {
    Box, Stack, Table, Text, Thead, Tbody, Th, Tr,
    TableContainer, Td, Skeleton, Input, Button,
    Select, SimpleGrid
} from "@chakra-ui/react";
import {Link, useParams, useSearchParams} from "react-router-dom";
import {Header} from "../components/Header";
import formatHoursAndMinutes from "../lib/helpers/formatHoursAndMinutes";

export default function AdminProjectDetailsContainer() {

    const {project_id} = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const [projectInfo, setProjectInfo] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [users, setUsers] = useState([]);
    const [, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);

    const page = useMemo(() => Number(searchParams.get("page")) || 1, [searchParams]);
    const q = useMemo(() => searchParams.get("q") || "", [searchParams]);
    const user_id = useMemo(() => searchParams.get("user_id") || "", [searchParams]);
    const sous_section = useMemo(() => searchParams.get("sous_section") || "", [searchParams]);
    const section_gid_asana = useMemo(() => searchParams.get("section_gid_asana") || "", [searchParams]);
    const start_date = useMemo(() => searchParams.get("start_date") || "", [searchParams]);
    const end_date = useMemo(() => searchParams.get("end_date") || "", [searchParams]);

    const isValidDateRange = useMemo(() => {
        if (!start_date || !end_date) return true;
        return new Date(start_date) <= new Date(end_date);
    }, [start_date, end_date]);

    const updateSearchParams = updates => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            Object.entries(updates).forEach(([key, value]) => {
                if (!value) next.delete(key);
                else next.set(key, String(value));
            });
            if (!("page" in updates)) next.set("page", "1");
            return next;
        });
    };

    const clearFilters = () => {
        setSearchParams(new URLSearchParams({page: "1"}));
    };

    useEffect(() => {
        axios.get("/users")
            .then(r => setUsers(r.data))
            .catch(console.error);
    }, []);

    useEffect(() => {
        axios.get(`/projects/${project_id}`)
            .then(r => setProjectInfo(r.data))
            .catch(() => setProjectInfo(null));
    }, [project_id]);

    useEffect(() => {

        if (!isValidDateRange) return;

        const queryParams = new URLSearchParams();

        if (page) queryParams.append("page", page);
        if (q) queryParams.append("q", q);
        if (user_id) queryParams.append("user_id", user_id);
        if (sous_section) queryParams.append("sous_section", sous_section);
        if (section_gid_asana) queryParams.append("section_gid_asana", section_gid_asana);
        if (start_date) queryParams.append("start_date", start_date);
        if (end_date) queryParams.append("end_date", end_date);

        setLoading(true);

        axios.get(`/projects/${project_id}/tasks?${queryParams.toString()}`)
            .then(res => {
                setTasks(res.data.tasks || []);
                setTotalPages(res.data.total_pages || 1);
            })
            .catch(console.error)
            .finally(() => setLoading(false));

    }, [project_id, page, q, user_id, sous_section, section_gid_asana, start_date, end_date, isValidDateRange]);

    const totalTimeHoursDisplay = useMemo(() => {
        const totalMinutes = tasks.reduce(
            (sum, t) => sum + Number(t.actual_time_minutes || 0),
            0
        );
        return formatHoursAndMinutes(totalMinutes / 60);
    }, [tasks]);

    return (
        <Box width={"full"} ml={{base: 0, md: "20px"}} px={{base: 2, md: 0}}>
            {projectInfo && (
                <>
                    <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                        <Header fontSize={{base: "xl", md: "2xl"}}>
                            Détail du projet (section)
                        </Header>

                        <Button as={Link}
                                to={`/admin/projects?id_client=${projectInfo.id_client}`}
                                variant="outline">
                            Retour
                        </Button>
                    </Stack>

                    <Box mb={4}>
                        <Text fontWeight="bold">
                            {projectInfo.client_name} — {projectInfo.nom}
                        </Text>
                    </Box>
                </>
            )}

            <Stack
                direction={{base: "column", md: "row"}}
                width={"auto"}
                justifyContent={{base: "center", md: "space-evenly"}}
                alignItems={{base: "stretch", md: "start"}}
                spacing={{base: 4, md: 6}}
            >

                <Stack direction={"column"} width={{base: "full", md: "min-content"}}>

                    <Text fontSize="xl" fontWeight="bold" mb={2}>
                        Total visible : {loading ? <Skeleton w="100px" h="24px"/> : totalTimeHoursDisplay}
                    </Text>

                    <TableContainer width={"full"} overflowX="auto">
                        <Table variant="simple" width={"full"} size={{base: "sm", md: "md"}}>
                            <Thead>
                                <Tr>
                                    <Th>Tâche</Th>
                                    <Th>Sous-section</Th>
                                    <Th>Responsable</Th>
                                    <Th isNumeric>Temps</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {loading ? (
                                    [...Array(8)].map((_, i) => (
                                        <Tr key={i}>
                                            <Td><Skeleton/></Td>
                                            <Td><Skeleton/></Td>
                                            <Td><Skeleton/></Td>
                                            <Td><Skeleton/></Td>
                                        </Tr>
                                    ))
                                ) : tasks.map(task => (
                                    <Tr key={task.task_gid}>
                                        <Td>{task.task_name}</Td>
                                        <Td>{task.sous_section_name ?? "-"}</Td>
                                        <Td>{task.assignee_name ?? "-"}</Td>
                                        <Td isNumeric>
                                            {formatHoursAndMinutes(Number(task.actual_time_minutes || 0) / 60)}
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>

                </Stack>

                <Stack direction={"column"} width={{base: "full", md: "360px"}}>
                    <Button variant="outline" onClick={clearFilters}>
                        Réinitialiser
                    </Button>

                    <Input
                        placeholder="Recherche"
                        value={q}
                        onChange={e => updateSearchParams({q: e.target.value})}
                    />

                    <Select
                        placeholder="Collaborateur"
                        value={user_id}
                        onChange={e => updateSearchParams({user_id: e.target.value})}
                    >
                        {users.map(u => (
                            <option key={u.user_gid} value={u.user_gid}>{u.name}</option>
                        ))}
                    </Select>

                    <SimpleGrid columns={2} spacing={4}>
                        <Input
                            type="date"
                            value={start_date}
                            onChange={e => updateSearchParams({start_date: e.target.value})}
                        />
                        <Input
                            type="date"
                            value={end_date}
                            onChange={e => updateSearchParams({end_date: e.target.value})}
                        />
                    </SimpleGrid>

                    {!isValidDateRange && (
                        <Text color="red.500">
                            La date de fin doit être postérieure à la date de début.
                        </Text>
                    )}

                </Stack>
            </Stack>
        </Box>
    );
}
